import React, { useCallback, useEffect, useState } from 'react';
import { Check, RefreshCw } from 'lucide-react';
import { hardwareApi, type OrderWelcomeDevice } from '../../services/hardwareApi';
import { RubyGemMark } from '../../components/RubyGemMark';

/**
 * LOCK RECEIVED BY DEVICE — per register on an order, before it is boxed.
 *
 * A till proves it has cached its Fresh/locked state by fetching it from
 * hardware-service, which stamps `deviceSeenAt`. A till that never checked in
 * would boot to PIN login in the store instead of "Hello", so RubyTill waits
 * for the tick before shipping. Re-reads every 15 s while any device is still
 * waiting.
 */

const POLL_MS = 15000;

const when = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** A device that is waiting for the welcome (Fresh or locked) and not yet activated. */
export const awaitsWelcome = (d: OrderWelcomeDevice) => !!d.registerId && !d.activation && (d.mode === 'fresh' || d.locked);

/** The words for one device. */
export function lockReceiptText(deviceSeenAt?: string): string {
  return deviceSeenAt ? `Lock received by device ✓ ${when(deviceSeenAt)}` : 'Waiting for the device to check in';
}

export const LockReceiptList: React.FC<{ orderId: string }> = ({ orderId }) => {
  const [devices, setDevices] = useState<OrderWelcomeDevice[] | null>(null);
  const [checking, setChecking] = useState(false);

  const read = useCallback(async () => {
    setChecking(true);
    try {
      const w = await hardwareApi.getOrderWelcome(orderId);
      setDevices(w.devices.filter(awaitsWelcome));
    } catch {
      /* keep what we had; the next read tries again */
    } finally {
      setChecking(false);
    }
  }, [orderId]);

  useEffect(() => { void read(); }, [read]);
  const waiting = !!devices?.some((d) => !d.deviceSeenAt);
  useEffect(() => {
    if (!waiting) return undefined;
    const t = setInterval(() => { void read(); }, POLL_MS);
    return () => clearInterval(t);
  }, [waiting, read]);

  if (!devices || devices.length === 0) return null;
  return (
    <div className="mt-3 rounded-lg border border-stone-200 bg-white p-3">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-stone-400">On the devices</p>
        <button
          type="button"
          onClick={() => { void read(); }}
          disabled={checking}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-2 text-[11.5px] font-semibold text-stone-600 hover:bg-stone-50 disabled:opacity-50"
        >
          {checking ? <RubyGemMark size={12} drawing color="currentColor" /> : <RefreshCw size={12} />} Check again
        </button>
      </div>
      {devices.map((d) => (
        <div key={d.unitId} className="flex flex-wrap items-center justify-between gap-2 border-b border-stone-100 py-1.5 text-[12px] last:border-b-0">
          <span className="font-semibold text-stone-900">{d.registerName || d.label}</span>
          <span className={`flex items-center gap-1 ${d.deviceSeenAt ? 'text-emerald-700' : 'text-stone-500'}`}>
            {d.deviceSeenAt && <Check size={12} />}
            {d.deviceSeenAt ? lockReceiptText(d.deviceSeenAt).replace(' ✓', '') : lockReceiptText()}
          </span>
        </div>
      ))}
      {waiting && (
        <p className="mt-1.5 text-[10.5px] leading-relaxed text-stone-500">Power each waiting device on, online, before it goes in the box.</p>
      )}
    </div>
  );
};

export default LockReceiptList;
